import { Order, OrderPosition } from '@@types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'

// Импортируем из entities только типы и данные
import { OrderItem, OrderItemPreview } from '@widgets/order-item'

import {
  ORDER_META_CANCELLED_REFUNDED_STATUSES,
  ORDER_META_REFUNDED_STATUSES,
  ORDER_META_STATUS_ASSEMBLED,
  ORDER_META_STATUS_ASSEMBLING,
  ORDER_META_STATUS_NEED_CONTACT,
  ORDER_META_STATUS_REPLACED,
  ORDER_META_STATUS_WAITING_ASSEMBLER,
  URL_PAGE_PRODUCT
} from '@shared/contants'
import { useLocalizedNavigate as useNavigate } from '@shared/lib/locale'
import Wrapper from '@shared/ui/wrapper'

// Импортируем компоненты из widgets

import OrderFooter from './OrderFooter'
import OrderHeader from './OrderHeader'
import OrderSummary from './OrderSummary'

import styles from './styles.module.scss'

interface OrderCardProps {
  order: Order
  isInitialOpen?: boolean
  onUpdate?: () => void
}

interface ProductGroup {
  title: string
  data: OrderPosition[]
}

export default function OrderCard ({ order, isInitialOpen, onUpdate }: OrderCardProps) {
  const navigate = useNavigate()
  const { t: to } = useTranslation('order')

  const [ isDetail, setIsDetail ] = useState(isInitialOpen ?? false)

  const handleDetailClick = () => {
    setIsDetail((p) => !p)
  }

  const handleProductClick = (productSlug?: string) => {
    if (!productSlug) return
    const link = generatePath(URL_PAGE_PRODUCT, { slug: productSlug })
    navigate(link)
  }

  const productGroups = useMemo<ProductGroup[]>(() => {
    if (!order.meta) return []

    const productsOrder = [ ...order.meta ].sort(
      (prev, curr) => (curr.id > prev.id ? 1 : -1)
    )

    const groups: ProductGroup[] = [
      {
        title: to('orderedProducts'),
        data: productsOrder.filter(
          (product) => product.status === ORDER_META_STATUS_WAITING_ASSEMBLER
        )
      },
      {
        title: to('collectingProducts'),
        data: productsOrder.filter(
          (product) => product.status === ORDER_META_STATUS_ASSEMBLING
        )
      },
      {
        title: to('waitingForDecision'),
        data: productsOrder.filter(
          (product) => product.status === ORDER_META_STATUS_NEED_CONTACT
        )
      },
      {
        title: to('collectedProducts'),
        data: productsOrder.filter(
          (product) => product.status === ORDER_META_STATUS_ASSEMBLED
        )
      },
      {
        title: to('refundedProducts'),
        data: productsOrder.filter((product) =>
          ORDER_META_REFUNDED_STATUSES.includes(product.status)
        )
      },
      {
        title: to('replacedProducts'),
        data: productsOrder.filter(
          (product) => product.status === ORDER_META_STATUS_REPLACED
        )
      },
      {
        title: to('cancelledProducts'),
        data: productsOrder.filter((product) =>
          ORDER_META_CANCELLED_REFUNDED_STATUSES.includes(product.status)
        )
      },
    ]

    return groups.filter((group) => group.data.length > 0)
  }, [ order.meta, to ])

  return (
    <div className={styles.root}>
      <Wrapper className={styles.root__wrapper}>
        <OrderHeader
          order={order}
          isDetail={isDetail}
          handleDetailClick={handleDetailClick}
          onSlotUpdate={onUpdate}
        />
        <div className={styles.root__items}>
          {isDetail ? (
            productGroups.length > 0 ? (
              productGroups.map((group, groupIndex) => (
                <div key={`group-${groupIndex}`} className={styles.root__group}>
                  <h4 className={styles.root__groupTitle}>{group.title}</h4>
                  {group.data.map((position, i) => (
                    <OrderItem 
                      key={`${position?.id}-${i}`} 
                      position={position} 
                      onNavToProduct={handleProductClick} 
                    />
                  ))}
                </div>
              ))
            ) : (
              order.meta?.map((position, i) => (
                <OrderItem key={`${position?.id}-${i}`} position={position} onNavToProduct={handleProductClick} />
              ))
            )
          ) : (
            <OrderItemPreview items={order.meta} />
          )}
        </div>
        {isDetail && <OrderSummary orderId={order.id} />}
        <OrderFooter order={order} onCancel={onUpdate} />
      </Wrapper>
    </div>
  )
}